import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Tab } from '@headlessui/react';
import {
  Users,
  Boxes,
  CalendarDays,
  Search,
  Plus,
  BadgeCheck,
  Wrench,
  Clock,
  ChevronRight,
  Edit3,
  Trash2,
  X,
  Save,
  Loader2,
} from 'lucide-react';
import dayjs from 'dayjs';
import { apiGet, apiPost, apiPut, apiDelete } from '@/services/api';
import { API_ROUTES, MATERIAL_STATUS_COLORS, type MaterialStatus, Role } from '@avgrp/shared';
import { useAuth } from '@/hooks/useAuth';
import { hasMinRole } from '@/utils/roles';

// ─── Types ──────────────────────────────────────────────────

interface QualificationEntry {
  qualification: { name: string; code: string };
  [key: string]: unknown;
}

interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  employeeId: string;
  email: string;
  role: string;
  status: string;
  qualifications: (QualificationEntry | string)[];
  currentShift: string | null;
  shiftAssignments?: { shift: { name: string; startTime: string; endTime: string } }[];
}

/** Extract display name from a qualification entry (handles both string and object forms) */
function getQualName(qual: QualificationEntry | string): string {
  if (typeof qual === 'string') return qual;
  return qual?.qualification?.name ?? 'Unknown';
}

interface Material {
  id: string;
  name: string;
  code: string;
  serialNumber?: string;
  category: string;
  materialType?: { name: string; code: string };
  materialTypeId?: string;
  status: MaterialStatus;
  location: string;
  base?: { name: string };
  lastMaintenance: string | null;
  nextMaintenance: string | null;
  notes?: string;
}

interface MaterialType {
  id: string;
  name: string;
  code: string;
}

interface Shift {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
  employeeCount?: number;
  assignments?: unknown[];
  date: string;
  minEmployees?: number;
  maxEmployees?: number;
}

interface MaterialFormData {
  name: string;
  serialNumber: string;
  notes: string;
  materialTypeId: string;
  nextMaintenance: string;
}

interface ShiftFormData {
  name: string;
  date: string;
  startTime: string;
  endTime: string;
  minEmployees: string;
  maxEmployees: string;
}

const emptyMaterialForm: MaterialFormData = {
  name: '',
  serialNumber: '',
  notes: '',
  materialTypeId: '',
  nextMaintenance: '',
};

const emptyShiftForm: ShiftFormData = {
  name: '',
  date: '',
  startTime: '',
  endTime: '',
  minEmployees: '',
  maxEmployees: '',
};

// ─── Status badge colors ────────────────────────────────────

const employeeStatusColors: Record<string, string> = {
  ACTIVE: 'bg-emerald-50 text-emerald-700',
  INACTIVE: 'bg-gray-100 text-gray-600',
  ON_LEAVE: 'bg-amber-50 text-amber-700',
  SUSPENDED: 'bg-red-50 text-red-700',
};

// ─── Component ──────────────────────────────────────────────

export function ResourcesPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Material form
  const [editingMaterial, setEditingMaterial] = useState<Material | null>(null);
  const [materialForm, setMaterialForm] = useState<MaterialFormData>(emptyMaterialForm);

  // Shift form
  const [editingShift, setEditingShift] = useState<Shift | null>(null);
  const [shiftForm, setShiftForm] = useState<ShiftFormData>(emptyShiftForm);

  // ─── Queries ────────────────────────────────────────────────

  const { data: employees, isLoading: isEmpLoading } = useQuery({
    queryKey: ['resources', 'employees'],
    queryFn: () => apiGet<{ data: Employee[] }>(`${API_ROUTES.RESOURCES}/employees`),
    select: (res) => res.data,
  });

  const { data: materials, isLoading: isMatLoading } = useQuery({
    queryKey: ['resources', 'materials'],
    queryFn: () => apiGet<{ data: Material[] }>(`${API_ROUTES.RESOURCES}/materials`),
    select: (res) => res.data,
  });

  const { data: shifts, isLoading: isShiftLoading } = useQuery({
    queryKey: ['resources', 'shifts'],
    queryFn: () => apiGet<{ data: Shift[] }>(`${API_ROUTES.RESOURCES}/shifts`),
    select: (res) => res.data,
  });

  const { data: materialTypes } = useQuery({
    queryKey: ['resources', 'material-types'],
    queryFn: () => apiGet<{ data: MaterialType[] }>(`${API_ROUTES.RESOURCES}/material-types`),
    select: (res) => res.data,
  });

  const employeeList = employees ?? [];
  const materialList = materials ?? [];
  const shiftList = shifts ?? [];
  const materialTypeList = materialTypes ?? [];
  const isLoading = isEmpLoading || isMatLoading || isShiftLoading;

  // ─── Material Mutations ─────────────────────────────────────

  const createMaterialMutation = useMutation({
    mutationFn: (data: MaterialFormData) =>
      apiPost(`${API_ROUTES.RESOURCES}/materials`, {
        name: data.name,
        materialTypeId: data.materialTypeId,
        baseId: user?.baseId,
        serialNumber: data.serialNumber || undefined,
        notes: data.notes || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resources', 'materials'] });
      setFormError(null);
      closeForm();
    },
    onError: (err: any) => {
      setFormError(err?.response?.data?.error || err?.message || 'Failed to create material');
    },
  });

  const updateMaterialMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: MaterialFormData }) =>
      apiPut(`${API_ROUTES.RESOURCES}/materials/${id}`, {
        name: data.name || undefined,
        notes: data.notes || undefined,
        nextMaintenance: data.nextMaintenance || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resources', 'materials'] });
      setFormError(null);
      closeForm();
    },
    onError: (err: any) => {
      setFormError(err?.response?.data?.error || err?.message || 'Failed to update material');
    },
  });

  const deleteMaterialMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`${API_ROUTES.RESOURCES}/materials/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resources', 'materials'] });
      setDeleteError(null);
    },
    onError: (err: any) => {
      setDeleteError(err?.response?.data?.error || err?.message || 'Failed to delete material');
    },
  });

  // ─── Shift Mutations ────────────────────────────────────────

  const createShiftMutation = useMutation({
    mutationFn: (data: ShiftFormData) =>
      apiPost(`${API_ROUTES.RESOURCES}/shifts`, {
        name: data.name,
        date: data.date,
        startTime: data.startTime,
        endTime: data.endTime,
        baseId: user?.baseId,
        minEmployees: data.minEmployees ? parseInt(data.minEmployees, 10) : undefined,
        maxEmployees: data.maxEmployees ? parseInt(data.maxEmployees, 10) : undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resources', 'shifts'] });
      setFormError(null);
      closeForm();
    },
    onError: (err: any) => {
      setFormError(err?.response?.data?.error || err?.message || 'Failed to create shift');
    },
  });

  const updateShiftMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: ShiftFormData }) =>
      apiPut(`${API_ROUTES.RESOURCES}/shifts/${id}`, {
        name: data.name || undefined,
        date: data.date || undefined,
        startTime: data.startTime || undefined,
        endTime: data.endTime || undefined,
        minEmployees: data.minEmployees ? parseInt(data.minEmployees, 10) : undefined,
        maxEmployees: data.maxEmployees ? parseInt(data.maxEmployees, 10) : undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resources', 'shifts'] });
      setFormError(null);
      closeForm();
    },
    onError: (err: any) => {
      setFormError(err?.response?.data?.error || err?.message || 'Failed to update shift');
    },
  });

  const deleteShiftMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`${API_ROUTES.RESOURCES}/shifts/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resources', 'shifts'] });
      setDeleteError(null);
    },
    onError: (err: any) => {
      setDeleteError(err?.response?.data?.error || err?.message || 'Failed to delete shift');
    },
  });

  // ─── Form helpers ─────────────────────────────────────────────

  function closeForm() {
    setShowForm(false);
    setEditingMaterial(null);
    setEditingShift(null);
    setMaterialForm(emptyMaterialForm);
    setShiftForm(emptyShiftForm);
    setFormError(null);
  }

  function handleAddClick() {
    closeForm();
    setShowForm(true);
  }

  function openEditMaterial(mat: Material) {
    closeForm();
    setEditingMaterial(mat);
    setMaterialForm({
      name: mat.name || '',
      serialNumber: mat.serialNumber || '',
      notes: mat.notes || '',
      materialTypeId: mat.materialTypeId || '',
      nextMaintenance: mat.nextMaintenance ? dayjs(mat.nextMaintenance).format('YYYY-MM-DD') : '',
    });
    setShowForm(true);
  }

  function openEditShift(shift: Shift) {
    closeForm();
    setEditingShift(shift);
    setShiftForm({
      name: shift.name || '',
      date: shift.date ? dayjs(shift.date).format('YYYY-MM-DD') : '',
      startTime: shift.startTime || '',
      endTime: shift.endTime || '',
      minEmployees: shift.minEmployees?.toString() || '',
      maxEmployees: shift.maxEmployees?.toString() || '',
    });
    setShowForm(true);
  }

  function handleSaveMaterial() {
    if (editingMaterial) {
      updateMaterialMutation.mutate({ id: editingMaterial.id, data: materialForm });
    } else {
      createMaterialMutation.mutate(materialForm);
    }
  }

  function handleSaveShift() {
    if (editingShift) {
      updateShiftMutation.mutate({ id: editingShift.id, data: shiftForm });
    } else {
      createShiftMutation.mutate(shiftForm);
    }
  }

  function handleDeleteMaterial(mat: Material) {
    if (window.confirm(`Are you sure you want to delete "${mat.name || mat.materialType?.name}"? This action cannot be undone.`)) {
      deleteMaterialMutation.mutate(mat.id);
    }
  }

  function handleDeleteShift(shift: Shift) {
    if (window.confirm(`Are you sure you want to delete shift "${shift.name}"? This action cannot be undone.`)) {
      deleteShiftMutation.mutate(shift.id);
    }
  }

  const isMaterialPending = createMaterialMutation.isPending || updateMaterialMutation.isPending;
  const isShiftPending = createShiftMutation.isPending || updateShiftMutation.isPending;

  const tabs = [
    { label: 'Employees', icon: Users, count: employeeList.length },
    { label: 'Materials', icon: Boxes, count: materialList.length },
    { label: 'Shifts', icon: CalendarDays, count: shiftList.length },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Resources</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage employees, materials, and shift schedules
          </p>
        </div>
      </div>

      {/* Delete error banner */}
      {deleteError && (
        <div className="flex items-center justify-between rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>{deleteError}</span>
          <button onClick={() => setDeleteError(null)} className="ml-2 text-red-500 hover:text-red-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Tabs */}
      <Tab.Group selectedIndex={activeTabIndex} onChange={(index) => { setActiveTabIndex(index); closeForm(); }}>
        <div className="card">
          <div className="flex items-center justify-between border-b border-gray-200 pb-4">
            <Tab.List className="flex gap-1">
              {tabs.map((tab) => (
                <Tab
                  key={tab.label}
                  className={({ selected }) =>
                    `flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors
                    ${
                      selected
                        ? 'bg-aviation-50 text-aviation-700'
                        : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                    }`
                  }
                >
                  <tab.icon className="h-4 w-4" />
                  {tab.label}
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                    {tab.count}
                  </span>
                </Tab>
              ))}
            </Tab.List>

            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="input-field w-64 pl-9"
                />
              </div>
              {(activeTabIndex === 0 || hasMinRole(user?.role, Role.MANAGER)) && (
                <button onClick={handleAddClick} className="btn-primary">
                  <Plus className="mr-2 h-4 w-4" />
                  Add
                </button>
              )}
            </div>
          </div>

          {/* ─── Inline Create/Edit Forms ──────────────────────── */}
          {showForm && (
            <div className="mt-4 rounded-xl border border-aviation-200 bg-aviation-50/30 p-5">
              {/* Employees tab — redirect message */}
              {activeTabIndex === 0 && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    Employees are managed from the <strong>Users</strong> page. Go to Users to add or edit employees.
                  </p>
                  <button onClick={closeForm} className="rounded-lg p-1 text-gray-400 hover:bg-gray-200">
                    <X className="h-5 w-5" />
                  </button>
                </div>
              )}

              {/* Materials form */}
              {activeTabIndex === 1 && hasMinRole(user?.role, Role.MANAGER) && (
                <>
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {editingMaterial ? 'Edit Material' : 'New Material'}
                    </h3>
                    <button onClick={closeForm} className="rounded-lg p-1 text-gray-400 hover:bg-gray-200">
                      <X className="h-5 w-5" />
                    </button>
                  </div>

                  {formError && (
                    <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                      {formError}
                    </div>
                  )}

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Name <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        value={materialForm.name}
                        onChange={(e) => setMaterialForm((p) => ({ ...p, name: e.target.value }))}
                        className="input-field"
                        placeholder="Material name"
                      />
                    </div>
                    {!editingMaterial && (
                      <>
                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-700">
                            Material Type <span className="text-red-400">*</span>
                          </label>
                          <select
                            value={materialForm.materialTypeId}
                            onChange={(e) => setMaterialForm((p) => ({ ...p, materialTypeId: e.target.value }))}
                            className="input-field"
                          >
                            <option value="">Select type...</option>
                            {materialTypeList.map((mt) => (
                              <option key={mt.id} value={mt.id}>
                                {mt.name} ({mt.code})
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-700">Serial Number</label>
                          <input
                            type="text"
                            value={materialForm.serialNumber}
                            onChange={(e) => setMaterialForm((p) => ({ ...p, serialNumber: e.target.value }))}
                            className="input-field"
                            placeholder="Optional serial number"
                          />
                        </div>
                      </>
                    )}
                    {editingMaterial && (
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Next Maintenance</label>
                        <input
                          type="date"
                          value={materialForm.nextMaintenance}
                          onChange={(e) => setMaterialForm((p) => ({ ...p, nextMaintenance: e.target.value }))}
                          className="input-field"
                        />
                      </div>
                    )}
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Notes</label>
                      <input
                        type="text"
                        value={materialForm.notes}
                        onChange={(e) => setMaterialForm((p) => ({ ...p, notes: e.target.value }))}
                        className="input-field"
                        placeholder="Optional notes"
                      />
                    </div>
                  </div>

                  <div className="mt-4 flex justify-end gap-2">
                    <button onClick={closeForm} className="btn-secondary">
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveMaterial}
                      disabled={
                        !materialForm.name ||
                        (!editingMaterial && !materialForm.materialTypeId) ||
                        isMaterialPending
                      }
                      className="btn-primary"
                    >
                      {isMaterialPending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="mr-2 h-4 w-4" />
                      )}
                      {editingMaterial ? 'Update' : 'Create'}
                    </button>
                  </div>
                </>
              )}

              {/* Shifts form */}
              {activeTabIndex === 2 && hasMinRole(user?.role, Role.MANAGER) && (
                <>
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {editingShift ? 'Edit Shift' : 'New Shift'}
                    </h3>
                    <button onClick={closeForm} className="rounded-lg p-1 text-gray-400 hover:bg-gray-200">
                      <X className="h-5 w-5" />
                    </button>
                  </div>

                  {formError && (
                    <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                      {formError}
                    </div>
                  )}

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Name <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        value={shiftForm.name}
                        onChange={(e) => setShiftForm((p) => ({ ...p, name: e.target.value }))}
                        className="input-field"
                        placeholder="Shift name"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Date <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="date"
                        value={shiftForm.date}
                        onChange={(e) => setShiftForm((p) => ({ ...p, date: e.target.value }))}
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Start Time <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="time"
                        value={shiftForm.startTime}
                        onChange={(e) => setShiftForm((p) => ({ ...p, startTime: e.target.value }))}
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        End Time <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="time"
                        value={shiftForm.endTime}
                        onChange={(e) => setShiftForm((p) => ({ ...p, endTime: e.target.value }))}
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Min Employees</label>
                      <input
                        type="number"
                        value={shiftForm.minEmployees}
                        onChange={(e) => setShiftForm((p) => ({ ...p, minEmployees: e.target.value }))}
                        className="input-field"
                        placeholder="Optional"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Max Employees</label>
                      <input
                        type="number"
                        value={shiftForm.maxEmployees}
                        onChange={(e) => setShiftForm((p) => ({ ...p, maxEmployees: e.target.value }))}
                        className="input-field"
                        placeholder="Optional"
                        min="0"
                      />
                    </div>
                  </div>

                  <div className="mt-4 flex justify-end gap-2">
                    <button onClick={closeForm} className="btn-secondary">
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveShift}
                      disabled={
                        !shiftForm.name ||
                        !shiftForm.date ||
                        !shiftForm.startTime ||
                        !shiftForm.endTime ||
                        isShiftPending
                      }
                      className="btn-primary"
                    >
                      {isShiftPending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="mr-2 h-4 w-4" />
                      )}
                      {editingShift ? 'Update' : 'Create'}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-aviation-200 border-t-aviation-600" />
            </div>
          ) : (
            <Tab.Panels className="mt-4">
              {/* Employees */}
              <Tab.Panel>
                <p className="mb-3 text-xs text-gray-400">
                  Manage employees from the Users page
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr>
                        <th className="table-header">Employee</th>
                        <th className="table-header">ID</th>
                        <th className="table-header">Role</th>
                        <th className="table-header">Status</th>
                        <th className="table-header">Qualifications</th>
                        <th className="table-header">Current Shift</th>
                        <th className="table-header"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {employeeList
                        .filter((e) => {
                          if (!searchQuery) return true;
                          const q = searchQuery.toLowerCase();
                          return (
                            `${e.firstName} ${e.lastName}`.toLowerCase().includes(q) ||
                            e.employeeId.toLowerCase().includes(q) ||
                            e.qualifications.some((qual) => getQualName(qual).toLowerCase().includes(q))
                          );
                        })
                        .map((emp) => (
                          <tr key={emp.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                            <td className="table-cell">
                              <div className="flex items-center gap-3">
                                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-aviation-100 text-xs font-semibold text-aviation-700">
                                  {emp.firstName[0]}{emp.lastName[0]}
                                </div>
                                <div>
                                  <p className="font-medium">{emp.firstName} {emp.lastName}</p>
                                  <p className="text-xs text-gray-400">{emp.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="table-cell font-mono text-gray-500">{emp.employeeId}</td>
                            <td className="table-cell text-gray-600">{emp.role}</td>
                            <td className="table-cell">
                              <span className={`badge ${employeeStatusColors[emp.status] || 'bg-gray-100 text-gray-600'}`}>
                                {emp.status.replace('_', ' ')}
                              </span>
                            </td>
                            <td className="table-cell">
                              <div className="flex flex-wrap gap-1">
                                {emp.qualifications.map((qual, idx) => (
                                  <span key={`${emp.id}-qual-${idx}`} className="badge bg-blue-50 text-blue-700">
                                    {getQualName(qual)}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td className="table-cell text-gray-500">
                              {emp.currentShift || (emp.shiftAssignments && emp.shiftAssignments.length > 0
                                ? `${emp.shiftAssignments[0]!.shift.name} (${emp.shiftAssignments[0]!.shift.startTime}-${emp.shiftAssignments[0]!.shift.endTime})`
                                : <span className="text-gray-300">-</span>
                              )}
                            </td>
                            <td className="table-cell">
                              <ChevronRight className="h-4 w-4 text-gray-400" />
                            </td>
                          </tr>
                        ))}
                      {employeeList.length === 0 && (
                        <tr>
                          <td colSpan={7} className="py-12 text-center text-sm text-gray-400">
                            No employees found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Tab.Panel>

              {/* Materials */}
              <Tab.Panel>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr>
                        <th className="table-header">Equipment</th>
                        <th className="table-header">Code</th>
                        <th className="table-header">Category</th>
                        <th className="table-header">Status</th>
                        <th className="table-header">Location</th>
                        <th className="table-header">Last Maintenance</th>
                        <th className="table-header">Next Maintenance</th>
                        {hasMinRole(user?.role, Role.MANAGER) && (
                          <th className="table-header">Actions</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {materialList
                        .filter((m) => {
                          if (!searchQuery) return true;
                          const q = searchQuery.toLowerCase();
                          const displayName = m.name || m.materialType?.name || '';
                          const displayCode = m.serialNumber || m.code || '';
                          const displayCategory = m.materialType?.code || m.category || '';
                          return (
                            displayName.toLowerCase().includes(q) ||
                            displayCode.toLowerCase().includes(q) ||
                            displayCategory.toLowerCase().includes(q)
                          );
                        })
                        .map((mat) => {
                          const statusColor = MATERIAL_STATUS_COLORS[mat.status] || '#6B7280';
                          return (
                            <tr key={mat.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                              <td className="table-cell">
                                <div className="flex items-center gap-3">
                                  <div className="rounded-lg bg-gray-100 p-2">
                                    <Wrench className="h-4 w-4 text-gray-500" />
                                  </div>
                                  <span className="font-medium">{mat.name || mat.materialType?.name || '-'}</span>
                                </div>
                              </td>
                              <td className="table-cell font-mono text-gray-500">{mat.serialNumber || mat.code}</td>
                              <td className="table-cell text-gray-600">{mat.materialType?.name || mat.category}</td>
                              <td className="table-cell">
                                <span
                                  className="badge"
                                  style={{ backgroundColor: `${statusColor}15`, color: statusColor }}
                                >
                                  {mat.status.replace('_', ' ')}
                                </span>
                              </td>
                              <td className="table-cell text-gray-500">{mat.location || mat.base?.name || '-'}</td>
                              <td className="table-cell text-gray-500">
                                {mat.lastMaintenance
                                  ? dayjs(mat.lastMaintenance).format('DD MMM YYYY')
                                  : '-'}
                              </td>
                              <td className="table-cell text-gray-500">
                                {mat.nextMaintenance
                                  ? dayjs(mat.nextMaintenance).format('DD MMM YYYY')
                                  : '-'}
                              </td>
                              {hasMinRole(user?.role, Role.MANAGER) && (
                                <td className="table-cell">
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={() => openEditMaterial(mat)}
                                      className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                                      title="Edit material"
                                    >
                                      <Edit3 className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteMaterial(mat)}
                                      disabled={deleteMaterialMutation.isPending}
                                      className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600"
                                      title="Delete material"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      {materialList.length === 0 && (
                        <tr>
                          <td colSpan={8} className="py-12 text-center text-sm text-gray-400">
                            No materials found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Tab.Panel>

              {/* Shifts */}
              <Tab.Panel>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {shiftList.map((shift) => (
                    <div
                      key={shift.id}
                      className="rounded-xl border border-gray-200 p-5 transition-colors hover:border-aviation-200 hover:bg-aviation-50/30"
                    >
                      <div className="mb-3 flex items-center justify-between">
                        <h3 className="font-semibold text-gray-900">{shift.name}</h3>
                        <div className="flex items-center gap-1">
                          <span className="badge bg-aviation-50 text-aviation-700">
                            {shift.employeeCount ?? shift.assignments?.length ?? 0} staff
                          </span>
                          {hasMinRole(user?.role, Role.MANAGER) && (
                            <>
                              <button
                                onClick={() => openEditShift(shift)}
                                className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                                title="Edit shift"
                              >
                                <Edit3 className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => handleDeleteShift(shift)}
                                disabled={deleteShiftMutation.isPending}
                                className="rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
                                title="Delete shift"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="space-y-2 text-sm text-gray-500">
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          <span>{shift.startTime} - {shift.endTime}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CalendarDays className="h-4 w-4" />
                          <span>{dayjs(shift.date).format('ddd, DD MMM YYYY')}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <BadgeCheck className="h-4 w-4" />
                          <span>{shift.employeeCount ?? shift.assignments?.length ?? 0} employees assigned</span>
                        </div>
                      </div>
                    </div>
                  ))}
                  {shiftList.length === 0 && (
                    <div className="col-span-full py-12 text-center text-sm text-gray-400">
                      No shifts found
                    </div>
                  )}
                </div>
              </Tab.Panel>
            </Tab.Panels>
          )}
        </div>
      </Tab.Group>
    </div>
  );
}
